// Escape a value for safe interpolation into an HTML string (the map tooltips
// build their markup by string concatenation, not Vue templates). Covers the
// five HTML metacharacters; always run API-sourced text (user names, countries)
// through this before it lands in an innerHTML-style sink.
export const escapeHtml = (value) =>
  String(value == null ? "" : value)
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;")
    .replace(/'/g, "&#39;");

// The magnitude at which a single decimal stops earning its place. Below it we
// keep one decimal (kept even when it's ".0", so "10.0" lines up with "9.5");
// at/above it the integer part is already three digits, so the decimal is
// noise and we show a whole grouped number instead.
const DECIMAL_CUTOFF = 100;

// Format a plain number with adaptive precision — the shared rule behind every
// figure that mixes small and large magnitudes (item counts in thousands,
// weight, distance): one decimal until the integer part hits three digits,
// then a whole grouped number. Returns the bare number; callers append their
// own unit/suffix.
export const formatDecimal = (value) => {
  const num = value || 0;
  if (num >= DECIMAL_CUTOFF) {
    return Math.round(num).toLocaleString("en");
  }
  return (Math.round(num * 10) / 10).toFixed(1);
};

// Format a distance in METRES for display. One shared rule for every surface
// (map tooltips, stats panel, leaderboard): >= 900 m as km via formatDecimal
// (so 10.0km but 150km), below 900 m plain metres.
export const formatDistance = (metres) => {
  const m = metres || 0;
  if (m >= 900) return formatDecimal(m / 1000) + "km";
  return Math.round(m) + "m";
};

// Compact a count for display: at/above `threshold` show it in thousands via
// formatDecimal with a "k" suffix (e.g. 9,500 -> "9.5k", 351,909 -> "352k");
// below the threshold, show the full number with thousands separators (e.g.
// "9,999").
export const formatCount = (value, threshold = 1000) => {
  const num = value || 0;
  if (num >= threshold) {
    return formatDecimal(num / 1000) + "k";
  }
  return num.toLocaleString("en", { useGrouping: true });
};

// Count distinct, non-empty values of `key` across a list of records. Used by
// Home.vue's strides_users/countries fallback when the endpoint doesn't populate
// those counts itself.
export const countDistinct = (list, key) => {
  const seen = new Set();
  (list || []).forEach((item) => {
    if (item && item[key]) seen.add(item[key]);
  });
  return seen.size;
};

// Turn a raw API item name into its display form: "Plastic - Bottle" ->
// "Bottle [Plastic]"; names without a " - " separator pass through unchanged,
// and empty/missing input falls back to "Unknown item" so a blank row reads as
// missing data rather than a rendering glitch. The first segment is the
// category; any remaining segments are rejoined as the item, so
// "Plastic - Bottle - Cap" -> "Bottle - Cap [Plastic]" rather than dropping the
// tail. Shared by the item breakdown so the display transform stays consistent.
export const renameItem = (itemName) => {
  const items = itemName?.split(" - ") || [];
  if (items.length < 2) {
    return items[0] || "Unknown item";
  }
  const [category, ...rest] = items;
  return `${rest.join(" - ")} [${category}]`;
};

// Format a weight in KILOGRAMS for display, shared by the stats panel and the
// fweight filter. Follows formatDecimal: one decimal below 100 kg (so "5.0kg"
// lines up with "5.5kg"), whole grouped kg above.
export const formatWeight = (kilograms) => formatDecimal(kilograms) + "kg";

// Format a duration in minutes as a compact "Xy Xd Xh Xmin" string, dropping
// zero units (leading and trailing — "1h", not "1h 0min"). Empty/zero shows as
// "0min". Months use "m" when a year precedes them (1y 3m — unambiguous),
// otherwise "mth" so a leading months figure isn't misread as minutes.
// Pass { maxUnit: "day" } to skip the year/month rollup and let days grow
// unbounded (e.g. the leaderboard's time column).
export const formatTime = (value, { maxUnit = "year" } = {}) => {
  if (!value && value !== 0) return "0min";

  // Minutes per unit. A year is 365 days and a month 30 days (approximations);
  // subtracting each unit from a running remainder rather than computing each
  // from totalMinutes independently keeps them consistent — otherwise a year's
  // 365 days folded modulo 30 leaks 5 phantom days into the days figure.
  const MIN_PER_YEAR = 60 * 24 * 365;
  const MIN_PER_MONTH = 60 * 24 * 30;
  const MIN_PER_DAY = 60 * 24;
  const MIN_PER_HOUR = 60;

  let remaining = Math.ceil(parseFloat(value));

  let years = 0;
  let months = 0;
  if (maxUnit !== "day") {
    years = Math.floor(remaining / MIN_PER_YEAR);
    remaining -= years * MIN_PER_YEAR;
    months = Math.floor(remaining / MIN_PER_MONTH);
    remaining -= months * MIN_PER_MONTH;
  }
  const days = Math.floor(remaining / MIN_PER_DAY);
  remaining -= days * MIN_PER_DAY;
  const hours = Math.floor(remaining / MIN_PER_HOUR);
  remaining -= hours * MIN_PER_HOUR;
  const minutes = remaining;

  const parts = [];
  if (years > 0) parts.push(`${years}y`);
  if (months > 0) parts.push(`${months}${years > 0 ? "m" : "mth"}`);
  if (days > 0) parts.push(`${days}d`);
  if (hours > 0) parts.push(`${hours}h`);
  if (minutes > 0) parts.push(`${minutes}min`);

  return parts.length > 0 ? parts.join(" ") : "0min";
};

// Deep value equality via JSON serialization. Meant for comparing API
// payloads (plain objects/arrays) where a re-fetch of the same endpoint
// serializes keys in the same order.
export const jsonEqual = (a, b) => JSON.stringify(a) === JSON.stringify(b);
